import { useCallback, useState } from 'react'
import ListSubheader from '@mui/material/ListSubheader'
import { useHuddle } from '../../contexts/HuddleContext'
import {
  huddleDevices,
  IAudioDeviceLists,
  listAudioDevices,
} from '../../helpers/huddle/huddleDevices'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import Menu, { OpenMenu } from '../Common/Menu'
import MenuItem from '../Common/MenuItem'
import styles from './Huddle.module.scss'

const NO_DEVICES: IAudioDeviceLists = { inputs: [], outputs: [] }

const deviceLabel = ({ label }: MediaDeviceInfo, index: number, kind: string) =>
  label || `${kind} ${index + 1}`

/** Which mic the huddle hears and which speakers it plays through. */
const HuddleDeviceMenu = () => {
  const { chooseMicrophone, chooseSpeaker } = useHuddle()
  const [devices, setDevices] = useState<IAudioDeviceLists>(NO_DEVICES)
  const [chosen, setChosen] = useState(() => huddleDevices.get())
  const [closeMenuCount, setCloseMenuCount] = useState(0)

  const load = useCallback(async () => {
    setChosen(huddleDevices.get())
    try {
      setDevices(await listAudioDevices())
    } catch {
      setDevices(NO_DEVICES)
    }
  }, [])

  const renderTrigger = useCallback(
    (open: OpenMenu, isOpen: boolean) => (
      <IconButton
        title="Microphone and speakers"
        box="sm"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={(event) => {
          void load()
          open(event)
        }}
      >
        <Icon name="settings" size="sm" />
      </IconButton>
    ),
    [load],
  )

  const pick = (choose: () => void) => {
    choose()
    setChosen(huddleDevices.get())
    setCloseMenuCount((count) => count + 1)
  }
  const options = (
    list: MediaDeviceInfo[],
    selectedId: string | null,
    kind: string,
    choose: (id: string | null) => void,
  ) => [
    <MenuItem key="default" selected={!selectedId} onClick={() => pick(() => choose(null))}>
      System default
    </MenuItem>,
    ...list.map((device, index) => (
      <MenuItem
        key={device.deviceId}
        selected={device.deviceId === selectedId}
        onClick={() => pick(() => choose(device.deviceId))}
      >
        {deviceLabel(device, index, kind)}
      </MenuItem>
    )),
  ]

  return (
    <Menu
      closeMenuCount={closeMenuCount}
      renderTrigger={renderTrigger}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      paperClassName={styles.deviceMenu}
    >
      <ListSubheader className={styles.deviceHeading}>Microphone</ListSubheader>
      {options(devices.inputs, chosen.inputId, 'Microphone', chooseMicrophone)}
      {devices.outputs.length > 0 && (
        <ListSubheader className={styles.deviceHeading}>Speaker</ListSubheader>
      )}
      {devices.outputs.length > 0 &&
        options(devices.outputs, chosen.outputId, 'Speaker', chooseSpeaker)}
    </Menu>
  )
}

export default HuddleDeviceMenu
